/**
 * The completion step every quotation creator passes through (Akhil, 2026-09-16: "the same will
 * be used in creating PO orders, so the data needs to be as correct as possible").
 *
 * Three places build a `create_quotation` body — the comparison allocation, the RFQ page's
 * contract lines, and the duplicate flow — and each carried a slightly different header. The
 * fields below have one source of truth and belong on every one of them, so they are filled here
 * rather than three times:
 *
 *   company · order_type · enquiry_type         mandatory since 2026-09-17: the enquiry's company and type, "Sales"
 *   currency · po_currency · po_exchange_rate   the company's currency; POs are raised in it
 *   total_po_cost                               Σ cost × qty in the COMPANY currency, as the panel shows
 *   customer_reference_no                       the enquiry's — the quotation quotes it back
 *   apply_discount_on                           the ERP's default, so the basis is never blank
 *   line_markup                                 the rate the lines were priced at, when uniform
 *   items[].tag                                 the route — `rfq` or `provision`
 *   items[].po_currency                         the company's, for a line with a PO rate but no
 *                                               currency (a contract or stock line)
 *
 * Nothing already set is overwritten.
 */

import { withoutInternalAnnotations } from './internalNotes'
import type { CreateQuotationPayload, QuotationLinePayload } from './quotationFromAllocation'

const round2 = (n: number) => Math.round(n * 100) / 100

const tagFor = (line: QuotationLinePayload): 'rfq' | 'provision' => (line.source.trim().toLowerCase() === 'supplier' ? 'rfq' : 'provision')

export function completeQuotationPayload(
  payload: CreateQuotationPayload,
  ctx: { baseCurrency: string; customerReferenceNo?: string | null; company?: string | null; enquiryType?: string | null },
): CreateQuotationPayload {
  const items = payload.items.map((line) => ({
    ...line,
    // Every creator's lines pass here, so this is where our notes are kept off the quotation for
    // good (Akhil, 2026-10-02) — whichever path built the line, and whatever it was copied from.
    ...(line.description !== undefined ? { description: withoutInternalAnnotations(line.description) } : {}),
    tag: line.tag ?? tagFor(line),
    ...(!line.po_currency && Number(line.po_currency_rate) > 0 ? { po_currency: ctx.baseCurrency } : {}),
  }))
  // In the COMPANY's currency (Akhil, 2026-09-16): the lines are costed in the quotation's, and
  // `exchange_rate` is company → quotation, so the sum comes home through it.
  const rate = Number.isFinite(payload.exchange_rate) && payload.exchange_rate > 0 ? payload.exchange_rate : 1
  const poCost = round2(items.reduce((sum, l) => sum + (l.cost_unit > 0 ? l.cost_unit * l.qty : 0), 0) / rate)
  const margins = new Set(items.map((l) => l.margin))
  const reference = (ctx.customerReferenceNo ?? '').trim()
  const company = (ctx.company ?? '').trim()
  return {
    ...payload,
    // Mandatory on the doctype since 2026-09-17 — the enquiry's, which every quotation belongs to.
    ...(payload.company === undefined && company !== '' ? { company } : {}),
    ...(payload.enquiry_type === undefined && (ctx.enquiryType ?? '').trim() !== '' ? { enquiry_type: (ctx.enquiryType ?? '').trim() } : {}),
    order_type: payload.order_type ?? 'Sales',
    currency: payload.currency ?? ctx.baseCurrency,
    po_currency: payload.po_currency ?? ctx.baseCurrency,
    po_exchange_rate: payload.po_exchange_rate ?? 1,
    total_po_cost: payload.total_po_cost ?? poCost,
    apply_discount_on: payload.apply_discount_on ?? 'Grand Total',
    ...(reference !== '' && payload.customer_reference_no === undefined ? { customer_reference_no: reference } : {}),
    ...(payload.line_markup === undefined && margins.size === 1 && items.length > 0 ? { line_markup: items[0].margin } : {}),
    items,
  }
}
